/**
 * Lot photos — user-taken photos of the ACTUAL items in a lot (slab shots,
 * corners, backs). Provenance today; the image source for eBay listings later.
 *
 * Storage: PUBLIC `lot-photos` bucket at "{user_id}/{lot_id}/{uuid}.jpg".
 * Public read is deliberate — eBay listing calls need publicly reachable
 * image URLs — and paths are unguessable UUIDs. Writes/deletes are RLS-scoped
 * to the owner's user-id path prefix (migration `lot_photos`).
 *
 * The lot row's "Photos" field (photos jsonb on cards + sealed) holds the
 * path array; images are compressed client-side (<=1600px JPEG) before upload.
 */
import { sb, CLOUD_USER } from "./supabase";
import { dataUrlToBlob } from "./storage";
import { processReceiptFile } from "./receipt";

const BUCKET = "lot-photos";

/** Public URL for a stored lot photo path. */
export function lotPhotoUrl(path: string): string {
  return sb.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
}

/** The lot row's photo path array (tolerant of null / legacy rows). */
export function lotPhotos(row: any): string[] {
  const v = row?.["Photos"];
  return Array.isArray(v) ? v.filter((x: any) => typeof x === "string" && x) : [];
}

/** Compress + upload one image file for a lot; returns the storage path. */
export async function uploadLotPhoto(file: File, lotId: string): Promise<string> {
  if (!CLOUD_USER) throw new Error("Not signed in");
  if (!file.type.startsWith("image/")) throw new Error(`${file.name}: not an image`);
  const { dataUrl } = await processReceiptFile(file); // resizes to <=1600px JPEG
  const blob = dataUrlToBlob(dataUrl);
  const id = (crypto && "randomUUID" in crypto)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const path = `${CLOUD_USER.id}/${lotId}/${id}.jpg`;
  const { error } = await sb.storage.from(BUCKET).upload(path, blob, {
    contentType: "image/jpeg",
    upsert: false,
  });
  if (error) throw error;
  return path;
}

/** Best-effort delete of a stored photo object (row update is authoritative). */
export async function deleteLotPhotoObject(path: string): Promise<void> {
  try { await sb.storage.from(BUCKET).remove([path]); } catch { /* best-effort */ }
}
